import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  RotateCcw,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { AxiosHttpClient } from "../../../../services/AxiosHttpService";
import dayjs from "dayjs";

interface UploadTask {
  id: string;
  fileName: string;
  originalFileName: string;
  status: "PENDING" | "PROCESSING" | "SUCCESS" | "FAILED";
  errorMessage?: string;
  progressPercent?: number;
  progressStep?: string;
}

interface UploadJob {
  id: string;
  status:
    | "PENDING"
    | "PROCESSING"
    | "COMPLETED"
    | "PARTIALLY_FAILED"
    | "FAILED";
  totalFiles: number;
  processedFiles: number;
  successFiles: number;
  failedFiles: number;
  tasks: UploadTask[];
  createdAt?: string;
}

interface UploadJobsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  getLegalDocuments: () => void;
}

const JOB_STATUS_LABELS: Record<UploadJob["status"], string> = {
  PENDING: "En attente",
  PROCESSING: "En cours",
  COMPLETED: "Terminé",
  PARTIALLY_FAILED: "Partiel",
  FAILED: "Échoué",
};

const JOB_STATUS_CLASSES: Record<UploadJob["status"], string> = {
  PENDING: "bg-muted text-muted-foreground",
  PROCESSING: "bg-blue-100 text-blue-700",
  COMPLETED: "bg-green-100 text-green-700",
  PARTIALLY_FAILED: "bg-amber-100 text-amber-700",
  FAILED: "bg-red-100 text-red-700",
};

export function UploadJobsPanel({
  open,
  onOpenChange,
  getLegalDocuments,
}: UploadJobsPanelProps) {
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [resumingTaskIds, setResumingTaskIds] = useState<Set<string>>(
    new Set()
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevActiveIdsRef = useRef<Set<string>>(new Set());
  const getLegalDocumentsRef = useRef(getLegalDocuments);

  useEffect(() => {
    getLegalDocumentsRef.current = getLegalDocuments;
  }, [getLegalDocuments]);

  const isActiveStatus = (status: UploadJob["status"]) =>
    status === "PENDING" || status === "PROCESSING";

  const doFetch = async () => {
    try {
      const data = await AxiosHttpClient.get<UploadJob[]>(
        "api/v1/docuveritas/legal-documents/upload/jobs"
      );
      const sorted = [...data].sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.localeCompare(a.createdAt);
        }
        return 0;
      });

      setJobs(sorted);

      // Detect jobs that just completed → refresh document list
      sorted.forEach((job) => {
        if (!isActiveStatus(job.status) && prevActiveIdsRef.current.has(job.id)) {
          getLegalDocumentsRef.current();
        }
      });

      prevActiveIdsRef.current = new Set(
        sorted.filter((j) => isActiveStatus(j.status)).map((j) => j.id)
      );

      // Stop polling once no active jobs remain
      if (!sorted.some((j) => isActiveStatus(j.status)) && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (e) {
      console.error("Error fetching upload jobs:", e);
    }
  };

  useEffect(() => {
    if (!open) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    setIsLoading(true);
    doFetch().finally(() => setIsLoading(false));
    intervalRef.current = setInterval(doFetch, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getTaskIcon = (status: UploadTask["status"]) => {
    switch (status) {
      case "SUCCESS":
        return <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />;
      case "FAILED":
        return <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />;
      case "PROCESSING":
        return (
          <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
        );
      default:
        return (
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        );
    }
  };

  const handleResumeTask = async (taskId: string) => {
    setResumingTaskIds((prev) => new Set(prev).add(taskId));
    try {
      await AxiosHttpClient.post(
        `api/v1/docuveritas/legal-documents/upload/tasks/${taskId}/resume`
      );
      await doFetch();
      // Le polling s'arrête quand plus aucun job n'est actif — le relancer
      if (!intervalRef.current) {
        intervalRef.current = setInterval(doFetch, 3000);
      }
    } catch (e) {
      console.error("Error resuming upload task:", e);
    } finally {
      setResumingTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Suivi des imports</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Aucun import enregistré.</p>
            </div>
          ) : (
            jobs.map((job) => {
              const progress =
                job.totalFiles > 0
                  ? Math.round((job.processedFiles / job.totalFiles) * 100)
                  : 0;
              const isExpanded = expandedJobId === job.id;

              return (
                <div key={job.id} className="border rounded-lg bg-background">
                  {/* Job header row — click to expand/collapse tasks */}
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors rounded-lg"
                    onClick={() =>
                      setExpandedJobId(isExpanded ? null : job.id)
                    }
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium truncate">
                          {job.tasks.length === 1
                            ? job.tasks[0].originalFileName ||
                              job.tasks[0].fileName
                            : `${job.totalFiles} fichier${
                                job.totalFiles > 1 ? "s" : ""
                              }`}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                            JOB_STATUS_CLASSES[job.status]
                          }`}
                        >
                          {isActiveStatus(job.status) && (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          )}
                          {JOB_STATUS_LABELS[job.status]}
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <Progress value={progress} className="h-1.5 flex-1" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {job.processedFiles}/{job.totalFiles}
                        </span>
                      </div>

                      {job.createdAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {dayjs(job.createdAt).format("DD/MM/YYYY HH:mm")}
                        </p>
                      )}
                    </div>

                    <div className="text-xs flex-shrink-0 text-right space-y-0.5">
                      {job.successFiles > 0 && (
                        <div className="text-green-600">
                          {job.successFiles} réussi
                          {job.successFiles > 1 ? "s" : ""}
                        </div>
                      )}
                      {job.failedFiles > 0 && (
                        <div className="text-destructive">
                          {job.failedFiles} erreur
                          {job.failedFiles > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Task list (expanded) */}
                  {isExpanded && job.tasks.length > 0 && (
                    <div className="border-t divide-y">
                      {job.tasks.map((task) => (
                        <div
                          key={task.id}
                          className="flex items-center gap-3 px-4 py-2.5 bg-muted/20"
                        >
                          {getTaskIcon(task.status)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">
                              {task.originalFileName || task.fileName}
                            </p>
                            {task.status === "PROCESSING" && task.progressStep && (
                              <p className="text-xs text-primary mt-0.5 truncate">
                                {task.progressStep}
                              </p>
                            )}
                          {task.status === "FAILED" && task.errorMessage && (
                              <p className="text-xs text-destructive mt-0.5">
                                {task.errorMessage}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {task.status === "PENDING" && "En attente"}
                            {task.status === "PROCESSING" &&
                              (task.progressPercent != null && task.progressPercent > 0
                                ? `${task.progressPercent}%`
                                : "En cours")}
                            {task.status === "SUCCESS" && "Réussi"}
                            {task.status === "FAILED" && "Échoué"}
                          </span>
                          {task.status === "FAILED" && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium text-foreground hover:bg-muted transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Reprendre l'import depuis le point d'échec (les étapes déjà payées ne sont pas refaites)"
                              disabled={resumingTaskIds.has(task.id)}
                              onClick={() => handleResumeTask(task.id)}
                            >
                              {resumingTaskIds.has(task.id) ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3 h-3" />
                              )}
                              Reprendre
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
